/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { DateTime } from '@cheqroom/date-time';
import { QueryClient, QueryFunction, QueryFunctionContext, QueryKey } from '@tanstack/react-query';

import Authentication from '../services/authentication';

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: process.env.NODE_ENV !== 'development',
		},
	},
});

/* 
	The query function context is needed to pass the AbortSignal to the fetch function.
	only the query function context has the signal property (since we can't abort a pending mutation).
*/
export const fetchGraphQLEndpoint = <TData, TVariables>(
	query: string,
	variables?: TVariables
): ((context?: QueryFunctionContext) => Promise<TData>) => {
	return async (context) => {
		const isQuery = !!context;

		const res = await fetch(process.env.GRAPHQL_URL as string, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: `Bearer ${await Authentication.getAccessToken()}`,
			},
			body: JSON.stringify({
				query,
				variables,
			}),
			signal: isQuery ? context?.signal : null,
		});

		const json = await res.json();

		if (json.errors && !json.data) {
			const { message } = json.errors[0] || 'Error..';
			throw new Error(message as string);
		}

		return { ...json.data, extensions: json.extensions };
	};
};

/**
 * Fix Python Dates from the API that do not have real timezone information
 *
 * This method will itterate thought response looking for strings that look like datetimes
 * Then strips the bad timezone
 *
 * Replicates logic from ApiDataSource
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fixPythonDates = (data: unknown): any => {
	if (DateTime.isISOString(data as string)) {
		return DateTime.stripTimeZone(data as string);
	} else if (data instanceof Object || Array.isArray(data)) {
		const obj = data as Record<string, unknown>;
		Object.entries(obj).forEach(([key, value]) => {
			obj[key] = fixPythonDates(value);
		});
	}
	return data;
};

export class PythonHTTPError extends Error {
	public readonly status: number;

	constructor(error: string, status: number) {
		super(error);
		this.status = status;
	}
}

const handleUnauthorizedError = (res: Response, json: Record<string, unknown>, anonymous: boolean): void => {
	const isUnauthorized =
		res.status === 401 ||
		json.status === '401 Unauthorized' ||
		(json.message as string) === 'You are not authenticated.';
	if (isUnauthorized && !anonymous) {
		Authentication.redirectToLogin();
		throw new PythonHTTPError((json.message as string) || 'You are not authenticated.', 401);
	}
};

export const fetchPythonEndpoint = <TData, TBody = BodyInit>(
	endpoint: string,
	body?: TBody,
	method: 'GET' | 'POST' = 'POST',
	anonymous = false
): (() => Promise<TData>) => {
	return async () => {
		let res;

		if (method === 'GET') {
			const url = new URL(`${process.env.API_URL as string}/${endpoint}`);
			Object.entries(body || {}).forEach(([key, value]) => {
				url.searchParams.append(key, value as string);
			});

			res = await fetch(url, {
				method,
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
					...(!anonymous ? { authorization: `Bearer ${await Authentication.getAccessToken()}` } : null),
				},
			});
		} else {
			res = await fetch(`${process.env.API_URL as string}/${endpoint}`, {
				method,
				headers: {
					...(body instanceof FormData ? null : { 'Content-Type': 'application/json' }),
					...(!anonymous ? { authorization: `Bearer ${await Authentication.getAccessToken()}` } : null),
				},
				...(body && { body: body instanceof FormData ? body : JSON.stringify(body) }),
			});
		}

		const json = (await res.json()) ?? {};

		handleUnauthorizedError(res, json as Record<string, unknown>, anonymous);

		if (json.httpError) {
			throw new PythonHTTPError((json.message || json.httpError) as string, res.status);
		}

		return fixPythonDates(json) as TData;
	};
};

// `fetch` doesn't support easy way for progress events for file uploads, so we need to use `XMLHttpRequest` instead
export const uploadFile = async <TData>(
	endpoint: string,
	formData: FormData,
	progressCallback?: (progressEvent: ProgressEvent) => void
): Promise<TData> => {
	const xhr = new XMLHttpRequest();
	xhr.open('POST', `${process.env.API_URL as string}/${endpoint}`, true);
	xhr.setRequestHeader('Accept', '*/*');
	xhr.setRequestHeader('authorization', `Bearer ${await Authentication.getAccessToken()}`);
	xhr.upload.onprogress = (progressEvent) => {
		progressCallback?.(progressEvent);
	};

	return await new Promise((resolve, reject) => {
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve(JSON.parse(xhr.responseText) as TData);
			} else {
				reject(new Error(`Error: ${xhr.status} - ${xhr.statusText}`));
			}
		};
		xhr.onerror = () => reject(new Error('Network error occurred'));
		xhr.send(formData);
	});
};

export const fetchQueryWithCacheFallback = async <QueryData>(
	queryKey: QueryKey,
	queryFn: QueryFunction
): Promise<QueryData> => {
	try {
		// Attempt to fetch fresh data
		const data = await queryClient.fetchQuery({
			queryKey,
			queryFn: queryFn,
		});
		return data as QueryData;
	} catch (error) {
		// Fetching failed, fallback to cached data if available
		const cachedData = queryClient.getQueryData<QueryData>(queryKey);
		if (cachedData) {
			// eslint-disable-next-line no-console
			console.warn('Fetch failed, returning cached data:', error);
			return cachedData;
		}
		// eslint-disable-next-line no-console
		console.error('Fetch failed and no cached data available:', error);
		throw error;
	}
};

type UpdateQueryDataParams<T> = {
	key: unknown[];
	updater: (data: T | undefined) => T | undefined;
	rollbackOnError?: boolean;
	mutation: (options: { onError: () => void }) => Promise<void> | void;
};

export function updateQueryDataWithRollback<T>({
	key,
	updater,
	rollbackOnError = true,
	mutation,
}: UpdateQueryDataParams<T>) {
	const previousData = queryClient.getQueryData<T>(key);

	queryClient.setQueryData<T>(key, updater);

	return mutation({
		onError: () => {
			if (rollbackOnError) {
				queryClient.setQueryData(key, previousData);
			}
		},
	});
}
